import { useState } from "react";
import { Plus, X, Mail, Check, Loader2, ArrowLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { invitationService } from "@/services/InvitationService";
import { useUserProfile } from "@/hooks/queries";

export default function InviteFriends() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: userProfile } = useUserProfile({
    staleTime: 0, // Disable caching for this component
    cacheTime: 0, // Don't cache at all
  });
  console.log("userProfile",userProfile);

  const userName =
    userProfile?.data?.user?.name ||
    userProfile?.user?.name ||
    userProfile?.name ||
    "User";
  const userFirstName = userName.split(" ")[0]; // Get first name only
  // Check if user came from dashboard
  const fromDashboard = location.state?.fromDashboard || false;
  
  const [inviteList, setInviteList] = useState([
    { id: 1, name: "", email: "" }
  ]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const addInvitePerson = () => {
    if (inviteList.length >= 10) {
      toast.error('Maximum 10 people can be invited');
      return;
    }
    
    const newId = Math.max(...inviteList.map(p => p.id)) + 1;
    setInviteList([...inviteList, { id: newId, name: "", email: "" }]);
    console.log(`Added invitation field for person ${newId}`);
  };

  const removeInvitePerson = (id) => {
    if (inviteList.length <= 1) return;
    
    setInviteList(inviteList.filter(person => person.id !== id));
    console.log(`Removed invitation field, current count: ${inviteList.length - 1}`);
  };

  const updatePerson = (id, field, value) => {
    setInviteList(inviteList.map(person => 
      person.id === id ? { ...person, [field]: value } : person
    ));
  };

  const submitInvitations = async (e) => {
    e.preventDefault();
    
    const validInvites = inviteList.filter(person => person.name.trim() && person.email.trim());
    
    if (validInvites.length === 0) {
      toast.error('Please add at least one person with valid name and email');
      return;
    }

    setIsLoading(true);
    
    try {
      // Prepare data in the format expected by backend
      const invitationsData = validInvites.map(person => ({
        name: person.name.trim(),
        email: person.email.trim()
      }));

      console.log('Sending invitation data to API:', JSON.stringify(invitationsData, null, 2));
      
      const response = await invitationService.sendInvitations(invitationsData);
      
      console.log('API Response:', response);
      toast.success(`Requests sent successfully to ${invitationsData.length} people!`);
      
      setShowSuccess(true);
      
    } catch (error) {
      console.error('Error sending invitations:', error);
      
      if (error?.response?.status === 422) {
        const errorMessage = error.response?.data?.message || 'Validation error occurred';
        toast.error(errorMessage);
      } else if (error?.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
        navigate('/login');
      } else {
        toast.error(error?.response?.data?.message || 'Failed to send requests. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToBookingConfirmation = () => {
    if (fromDashboard) {
      // If came from dashboard, go back to dashboard
      navigate('/user-dashboard');
    } else {
      // If came from signup flow, go to booking confirmation
      navigate('/booking-success');
    }
  };

  const handleInviteMorePeople = () => {
    setShowSuccess(false);
    setInviteList([{ id: 1, name: "", email: "" }]);
  };

  const handleGoBack = () => {
    navigate('/user-dashboard');
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="mt-8 bg-secondary border border-secondary-border rounded-lg p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-600 text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-green-900 mb-2">Requests Sent!</h3>
            <p className="text-green-700 mb-6">Your friends and family will receive their requests shortly and can start contributing questions.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {!fromDashboard && (
                <button onClick={handleInviteMorePeople} className="btn-secondary">
                  <Plus className="mr-2" size={16} />
                  Ask More People
                </button>
              )}
              <button onClick={handleGoToBookingConfirmation} className="btn-primary">
                {fromDashboard ? 'Go to Dashboard' : 'Continue to Booking Confirmation'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster richColors position="top-right" />
      <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {fromDashboard && (
          <button
            onClick={handleGoBack}
            className="flex items-center text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Dashboard
          </button>
        )}
        
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">Ask your friends and family</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Ask up to 10 people to contribute meaningful questions for your legacy story. 
            Their questions will help guide your interview and make it more personal.
          </p>
        </div>

        <div className="bg-secondary rounded-xl border border-secondary-border p-8">
          <form onSubmit={submitInvitations}>
            <div className="space-y-6">
              {inviteList.map((person, index) => (
                <div key={person.id} className="invite-item">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[18px] font-semibold text-foreground">Person {index + 1}</h3>
                    {inviteList.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeInvitePerson(person.id)}
                        className="text-destructive hover:text-destructive/80"
                      >
                        <X size={20} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[18px] font-semibold text-foreground mb-1">Name</label>
                      <input 
                        type="text" 
                        className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]" 
                        placeholder="Enter their name" 
                        value={person.name}
                        onChange={(e) => updatePerson(person.id, 'name', e.target.value)}
                        required 
                      />
                    </div>
                    <div>
                      <label className="block text-[18px] font-semibold text-foreground mb-1">Email</label>
                      <input 
                        type="email" 
                        className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:text-[#7C797980]" 
                        placeholder="example@email.com" 
                        value={person.email}
                        onChange={(e) => updatePerson(person.id, 'email', e.target.value)}
                        required 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <button 
                type="button" 
                onClick={addInvitePerson} 
                className="btn-secondary flex items-center justify-center"
                disabled={inviteList.length >= 10}
              >
                <Plus className="mr-2" size={16} />
                Add Another Person
              </button>
              <div className="text-sm text-muted-foreground flex items-center">
                <span>{inviteList.length}</span> of 10 people added
              </div>
            </div>

            {/* Preview Section */}
            <div className="mt-8 p-6 bg-secondary border border-secondary-border rounded-lg">
              <h4 className="text-[18px] font-semibold text-foreground mb-3">What they&apos;ll receive:</h4>
              <div className="bg-secondary border border-secondary-border p-4 rounded-lg">
                <div className="text-sm text-muted-foreground mb-2">Email Preview:</div>
                <div className="font-semibold text-foreground mb-1">Help Create {userFirstName}&apos;s Legacy Story</div>
                <div className="text-sm text-muted-foreground">
                  {userName} has asked you to contribute questions for their legacy recording session. 
                  Your thoughtful questions will help capture the stories that matter most.
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-border">
              <button type="submit" className="btn-primary w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Sending Requests...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2" size={20} />
                    Send Requests
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
} 